import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './index.css';
import { Col, Container, Row } from 'react-bootstrap';
import { Button, Form, Input, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { setCustomer } from '../StorageBox/store/actionCreators';
import { setPackagesCustomer } from '../Packages/store/actionCreators';


import { login, packagesLogin } from '../../data/API';
import { setMyData, setToken } from '../../utils/token';
import { resetStore } from '../StorageBox/store/actionCreators';
import { resetReduxStore } from '../Packages/store/actionCreators';


const Login = (props) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Moving Box Rental");
  const options = ["Moving Box Rental", "Storage On-Demand"];

  useEffect(() => {
    dispatch({ type: 'RESET_REDUX_STORE' });
    dispatch(resetStore());
    dispatch(resetReduxStore());
    localStorage.removeItem('storageState');
    localStorage.removeItem('package');
    localStorage.removeItem('total');
    localStorage.removeItem('movingSupplies');
    localStorage.removeItem('tax');
    localStorage.removeItem('packagingSupplies');
    localStorage.removeItem('isCartEmpty');
    sessionStorage.removeItem('orderType', 'storage');
    sessionStorage.removeItem('orderType', 'packages');

    props.setShowHideHeader(true);
    props.setshowHideFooter(false);
    props.setshowHideinnerFooter(false);
    
    
    const referrer = document.referrer;
    const isFromOrderConfirmation = 
      referrer.includes('/storage-box/order-confirmation') || 
      location.state?.from === 'orderConfirmation';
    
    if (isFromOrderConfirmation) {
      message.success({
        content: 'Your order successfully placed please login to continue',
        duration: 5
      });
    }
  }, []);

  const onSubmit = async () => {
  if (!selectedOption) {
    message.warning('Please select a login option (Storage On-Demand or Moving Box Rental)');
    return;
  }

  try {
    const formValues = await form.validateFields();
    setFormSubmitted(true);
    
    let loginResponse;
    
    if (selectedOption === "Storage On-Demand") {
      loginResponse = await login(formValues);
      
      if (loginResponse?.data?.success) {
        const userData = loginResponse.data.data;
        
        setToken(userData.token);
        setMyData({
          email: userData.email,
          id: userData.id,
          name: userData.name
        });
        
        dispatch(setCustomer(userData));
        
        // Store login type in localStorage for persistence across page refreshes
        localStorage.setItem('loginType', 'Storage On-Demand');
        
        message.success('Logged in successfully...!');
        
        if (userData.agreedTerms) {
          navigate('/my-account?tab=account');
        } else {
          navigate('/storage-box/how-it-works');
        }
      } else {
        const errorMsg = loginResponse?.data?.message || 'Unable to login. Please try again...!';
        
        // Handle email verification error for Storage On-Demand only
        if (loginResponse?.data?.errorCode === "CLIENT_ERROR" && loginResponse?.data?.message?.includes('email is not verified')) {
          message.warning('Your email is not verified. Please check your inbox for a verification link.');
          navigate('/storage-box/email/confirmation');
        } else {
          message.error(errorMsg);
        }
        setFormSubmitted(false); 
      }
    } 
    else if (selectedOption === "Moving Box Rental") {
      const packagesData = {
        email_address: formValues.email,
        password: formValues.password
      };
      
      loginResponse = await packagesLogin(packagesData);
      
      if (loginResponse?.data?.status === "success") {
        const userData = loginResponse.data.data;
        
        setToken(userData.token);
        setMyData({
          email: userData.email,
          id: userData.id,
          name: `${userData.first_name} ${userData.last_name}`.trim()
        });
        
        dispatch(setPackagesCustomer({
          userId: userData.id,
          userName: `${userData.first_name} ${userData.last_name}`.trim(),
          token: userData.token,
          email: userData.email
        }));
        
        // Store login type in localStorage for persistence across page refreshes
        localStorage.setItem('loginType', 'Moving Box Rental');
        
        message.success('Logged in successfully...!');
        
        navigate('/my-account?tab=account');
      } else {
        const errorMsg = loginResponse?.data?.message || 'User not found. Please try again...!';
        message.error(errorMsg);
        setFormSubmitted(false); 
      }
    }
  } catch (err) {
    // Handle email verification error in catch block for Storage On-Demand only
    if (selectedOption === "Storage On-Demand" && err.response && err.response.data) {
      const { errorCode, message: errorMessage } = err.response.data;
      if (errorCode === "CLIENT_ERROR" && errorMessage.includes('email is not verified')) {
        message.warning('Your email is not verified. Please check your inbox for a verification link.');
        navigate('/storage-box/email/confirmation');
        setFormSubmitted(false);
        return;
      }
    }
    
    message.error('User not found. Please try again...!');
    setFormSubmitted(false); 
  }
};

const handleSelect = (option) => {
  setSelectedOption(option);
};

  return (
    <>      
      <Container fluid={true} className="p-0 mt-2 mb-3">
        <div className="login-header px-3">
          <div className="login-header-content">
            <p className="header text-inherit shadow-none border-none mb-4">Login</p>
            <p className="sub-header text-inherit shadow-none border-none" style={{ color: 'black', fontSize: '14px' }}>Note: If you use both services, you can select either login type.</p>
            
            {/* Pills Section */}
            <div className="login-pills-container mt-3">
              <p className="pills-label mb-3" style={{ color: 'black', fontFamily: 'inherit' }}>Choose Login Type</p>
              <div className="pills-wrapper d-flex gap-2 flex-wrap justify-content-center">
                {options.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`login-pill ${selectedOption === option ? 'active' : ''}`}
                    onClick={() => handleSelect(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>

      <Container fluid={true} className="signup-form-bg py-5">
        {!selectedOption && (
          <div className="text-center mb-3">
            <p className="text-muted">Please select an option to proceed</p> 
          </div>
        )}
        <Row className="align-items-center justify-content-center">
          <Col xxl={5} xl={5} lg={5} md={6} sm={12} xs={12}>
            <div className={`signup-form px-2 ${!selectedOption ? 'form-disabled' : ''}`}>
              <Form form={form} layout="vertical" autoComplete="off">
                <Row>
                  <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12} className={'pb-0'}>
                    <Form.Item
                      name="email"
                      label="Email"
                      validateTrigger="onBlur"
                      rules={[
                        { required: true, message: 'Please input the email address' },
                        { type: 'email', message: 'Please enter a valid email address' }
                      ]}
                      validateStatus={showValidation ? 'success' : ''}>
                      <Input
                        size={'large'}
                        type="text"
                        placeholder=""
                        className="signup-form-input"
                        disabled={!selectedOption}
                        onChange={() => {
                          setShowValidation(true);
                          let el = document.getElementById('email_address_help');
                          if (el) {
                            el.classList.add('d-none');
                          }
                        }}
                        onBlur={() => {
                          setShowValidation(false);
                          let el = document.getElementById('email_address_help');
                          if (el) {
                            el.classList.remove('d-none');
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12} className={'pb-0'}>
                    <div className="position-relative">
                      <Form.Item
                        className="mb-0"
                        label="Password"
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: 'Please enter your password!'
                          }
                        ]}>
                        <Input
                          size={'large'}
                          type={!showPassword ? 'password' : 'text'}
                          placeholder="******"
                          className={`signup-form-input bluebox-placeholder ${showPassword ? "mb-3" : ""}`}
                          disabled={!selectedOption}
                        />
                      </Form.Item>
                      <div 
                        className={`toggle-icon ${!selectedOption ? 'disabled' : ''}`} 
                        onClick={() => selectedOption && setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeInvisibleOutlined className="ant-eye-icon" />
                        ) : (
                          <EyeOutlined className="ant-eye-icon" />
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12} className="text-right pb-0">
                    <p className="forgot-password mb-4 mt-0">
                      Forgot you password?{' '}
                      <span
                        className={`navigate-link ${!selectedOption ? 'disabled-link' : ''}`}
                        onClick={() => {
                          if (selectedOption) {
                            if (selectedOption === "Storage On-Demand") {
                              navigate('/storage-box/reset-password/generate');
                            } else if (selectedOption === "Moving Box Rental") {
                              navigate('/reset-password/generate');
                            }
                          }
                        }}>
                        Click here to reset it
                      </span>
                    </p>

                    <Form.Item>
                      <Button
                        size="large"
                        type="primary"
                        htmlType="submit"
                        className="signup-register-btn"
                        onClick={onSubmit}
                        loading={isFormSubmitted}
                        >
                        Login
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </div>
          </Col>
        </Row>
        <div className="signup-form-divider">
          <div className="divider"></div>
        </div>
        {selectedOption && (
          <div className="signup-footer pt-3">
            <p className="mb-0 sub-text">
              Don&#39;t have an account?{' '}
              <span 
                className="navigate-link" 
                onClick={() => {
                  if (selectedOption === "Storage On-Demand") {
                    navigate('/storage-box/register');
                  } else if (selectedOption === "Moving Box Rental") {
                    navigate('/packages');
                  }
                }}
              >
                {selectedOption === "Storage On-Demand" ? "Signup here" : "Place an order to create an account"}
              </span>
            </p>
          </div>
        )}
      </Container>
    </>
  );
};

export default Login;

Login.propTypes = {
  setShowHideHeader: PropTypes.func,
  setshowHideFooter: PropTypes.func,
  setshowHideinnerFooter: PropTypes.func
};